import {
  HTTP_INTERCEPTORS,
  HttpClient,
  type HttpHandler,
  type HttpInterceptor,
  type HttpRequest,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { type ApplicationConfig, ErrorHandler, Injectable, importProvidersFrom, inject, isDevMode, provideZonelessChangeDetection } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { provideRouter } from '@angular/router';
import { MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { getMsalInterceptorConfig, SharedAzureAuthModule } from '@dtis/angular';
import { provideTransloco, type Translation, type TranslocoLoader } from '@jsverse/transloco';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

@Injectable()
class NoCacheHeadersInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler) {
    const authReq = req.clone({
      setHeaders: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return next.handle(authReq);
  }
}

@Injectable({ providedIn: 'root' })
class GlobalErrorHandler implements ErrorHandler {
  private readonly snackbar = inject(MatSnackBar);
  handleError(error: unknown) {
    console.error('Unhandled error:', error);
    this.snackbar.open(error instanceof Error ? error.message : 'An unexpected error occurred', 'Close', {
      duration: 5000,
    });
  }
}

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheHeadersInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideRouter(appRoutes),
    provideTransloco({
      config: {
        availableLangs: ['en'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    importProvidersFrom(SharedAzureAuthModule.forRoot(environment)),
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: getMsalInterceptorConfig({
        interactionType: InteractionType.Redirect,
        protectedResources: [
          {
            route: '/ultrasonic/*',
            scopes: environment.apiScope,
          },
          {
            route: '/api/*',
            scopes: environment.apiScope,
          },
        ],
      }),
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
};
